import {
  PointOfInterest,
  PointOfInterestsDocument,
  PointOfInterestTemplate,
  useUpdatePointOfInterestMutation,
} from '@/__generated__/graphql';
import { DEFAULT_REVIEW_STATUS } from '@/constants';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useMemo } from 'react';
import { PillMenu } from '@/components/shared/PillMenu';
import { PillStatus } from '@/components/shared/Pill/types';
import { useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';

const getPillStatus = (status: string): PillStatus => {
  switch (status) {
    case 'Pending review':
      return 'info';
    case 'GVI':
      return 'failed';
    case 'CVI':
      return 'warning';
    case 'No Follow Up':
      return 'success';
    default:
      return 'info';
  }
};

export const ReviewAction = ({
  pointOfInterest,
  templateOfSelectedPoi,
}: {
  pointOfInterest: PointOfInterest;
  templateOfSelectedPoi: Partial<PointOfInterestTemplate> | undefined;
}) => {
  const [updateMutation, { loading }] = useUpdatePointOfInterestMutation();
  const { showSnackBar } = useSnackBarMessage({});
  const setIsLoadingPOIsForReviewReport = useSetAtom(state.loadingPOIsForReviewReport);
  const actionField = useMemo(() => {
    if (!templateOfSelectedPoi?.fields) return null;
    return templateOfSelectedPoi?.fields?.find((field) => field.key === 'review_action');
  }, [templateOfSelectedPoi?.fields]);

  const action = useMemo(() => {
    if (!actionField) return DEFAULT_REVIEW_STATUS;
    // using or to handle case of empty string
    return (
      pointOfInterest.templateData?.find((td) => td.id === actionField.id)?.value ||
      DEFAULT_REVIEW_STATUS
    );
  }, [pointOfInterest.templateData, actionField]);

  if (!actionField) return <></>;

  const handleChange = async (action: string) => {
    const initialTemplateData = templateOfSelectedPoi?.fields?.reduce(
      (accumulator: { [key: string]: string }, templateField) => {
        if (accumulator[templateField.id]) return accumulator;
        const templateDataItem = pointOfInterest.templateData?.find(
          (td) => td.id === templateField.id
        );
        return {
          ...accumulator,
          [templateField.id]: templateDataItem?.value ?? '',
        };
      },
      {} as { [key: string]: string }
    );

    const templateData = Object.entries(initialTemplateData ?? {}).map(([key, value]) => ({
      id: key,
      value: key === actionField.id ? action : value,
    }));
    try {
      setIsLoadingPOIsForReviewReport(true);
      await updateMutation({
        variables: {
          input: {
            pointOfInterestId: pointOfInterest.id,
            templateId: templateOfSelectedPoi?.id ?? '',
            templateData,
            lastModifiedFor: 'Update Review Action',
          },
        },
        refetchQueries: [PointOfInterestsDocument],
      });
      showSnackBar('Review action updated successfully', 'success');
    } catch {
      setIsLoadingPOIsForReviewReport(false);
      showSnackBar('Failed to update review action. Please contact support', 'error');
    }
  };

  return (
    <PillMenu
      key={`${pointOfInterest.id}`}
      options={actionField.options}
      initialValue={action}
      loading={loading}
      loadingLabel="Updating..."
      onChange={handleChange}
      getPillStatus={getPillStatus}
    />
  );
};
